// API client for backend communication

const API_URL = import.meta.env.VITE_API_URL || 'https://notes-api.nicecoffee.qzz.io';

export interface ApiError {
  code: string;
  message: string;
  details?: any;
}

export class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor(baseUrl: string = API_URL) {
    this.baseUrl = baseUrl;
    this.loadTokens();
  }

  private loadTokens() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  private saveTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  private clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ data?: T; error?: ApiError }> {
    // Check if token needs refresh (before it expires)
    await this.checkAndRefreshToken();

    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        // If unauthorized and we have a refresh token, try to refresh
        if (response.status === 401 && this.refreshToken && endpoint !== '/api/auth/refresh') {
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            // Retry the original request
            return this.request(endpoint, options);
          }
        }

        return { error: data.error };
      }

      // Update last activity time
      localStorage.setItem('lastActivity', Date.now().toString());

      return { data };
    } catch (error) {
      console.error('API request error:', error);
      return {
        error: {
          code: 'NETWORK_ERROR',
          message: 'Failed to connect to server',
        },
      };
    }
  }

  private async checkAndRefreshToken(): Promise<void> {
    if (!this.accessToken || !this.refreshToken) {
      return;
    }

    try {
      // Decode token to check expiration
      const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - now;

      // Refresh if token expires in less than 7 days
      if (timeUntilExpiry < 7 * 24 * 60 * 60) {
        await this.refreshAccessToken();
      }
    } catch (error) {
      console.error('Token check error:', error);
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: this.refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      this.saveTokens(data.accessToken, data.refreshToken);
      return true;
    }

    this.clearTokens();
    return false;
  }

  // Auth methods
  async register(email: string, password: string) {
    const result = await this.request<{
      user: { id: string; email: string };
      accessToken: string;
      refreshToken: string;
    }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (result.data) {
      this.saveTokens(result.data.accessToken, result.data.refreshToken);
    }

    return result;
  }

  async login(email: string, password: string) {
    const result = await this.request<{
      user: { id: string; email: string };
      accessToken: string;
      refreshToken: string;
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (result.data) {
      this.saveTokens(result.data.accessToken, result.data.refreshToken);
    }

    return result;
  }

  async logout() {
    if (this.refreshToken) {
      await this.request('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });
    }

    this.clearTokens();
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  // Notes methods
  async getNotes(params?: {
    category?: string;
    categoryId?: string;
    tags?: string;
    limit?: number;
    offset?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.tags) queryParams.append('tags', params.tags);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    return this.request<{ notes: any[] }>(`/api/notes${query ? `?${query}` : ''}`);
  }

  async getNote(id: string) {
    return this.request<{ note: any }>(`/api/notes/${id}`);
  }

  async createNote(note: any) {
    return this.request<{ note: any }>('/api/notes', {
      method: 'POST',
      body: JSON.stringify(note),
    });
  }

  async updateNote(id: string, note: any) {
    return this.request<{ note: any }>(`/api/notes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(note),
    });
  }

  async deleteNote(id: string) {
    return this.request<{ message: string }>(`/api/notes/${id}`, {
      method: 'DELETE',
    });
  }

  // Sync methods
  async getSyncChanges(lastSyncTime?: string) {
    const params = new URLSearchParams();
    if (lastSyncTime) {
      params.append('since', lastSyncTime);
    }

    return this.request<{
      changes: {
        notes: any[];
        categories: any[];
        tags: any[];
        notifications: any[];
      };
      timestamp: string;
    }>(`/api/sync/changes${params.toString() ? `?${params.toString()}` : ''}`);
  }

  async applySyncChanges(changes: {
    notes?: any[];
    categories?: any[];
    tags?: any[];
    notifications?: any[];
  }) {
    return this.request<{
      applied: number;
      conflicts: any[];
    }>('/api/sync/apply', {
      method: 'POST',
      body: JSON.stringify(changes),
    });
  }

  async resolveConflict(resourceType: string, resourceId: string, resolution: 'local' | 'remote') {
    return this.request<{ resolved: boolean }>('/api/sync/resolve-conflict', {
      method: 'POST',
      body: JSON.stringify({ resourceType, resourceId, resolution }),
    });
  }
  async getNoteStats(category?: string) {
    const query = category ? `?category=${category}` : '';
    return this.request<{ count: number; uncategorizedCount: number }>(`/api/notes/stats${query}`);
  }
}

export const apiClient = new ApiClient();
